import Request from '../../utilities/Request';
import generateUuid from '../../utilities/generateUuid';
import {
	addMealIdeaGroupToGroceryList,
	addMealIdeaToGroceryList,
	createMeal,
	fetchGroceryLists,
	removeMealIdeaIngredientFromGroceryList,
	ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS,
	REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS,
} from './calendar';
import { foodChangesReceived, isFoodActive, refreshFood, whenFoodRefreshed } from './food';
import { selectMealIdeasIndex } from '../food/selectors';

/**
 * Meal ideas: the JS side of the `meal_idea` API domain (`models/components/api/meal_idea_domain.php`).
 * Every mutation there answers with the same index shape the GET does, so one success action covers
 * them all, and the food store takes that index in (food/legacyChanges.js), since the ideas are its
 * (selectMealIdeasIndex). The field names posted here are the contract with that domain; the spec
 * pins them.
 */

export const MEAL_IDEAS_LOADED = 'MEAL_IDEAS_LOADED';
export const MEAL_IDEAS_FAIL = 'MEAL_IDEAS_FAIL';
export const MEAL_IDEAS_SAVE_BEGIN = 'MEAL_IDEAS_SAVE_BEGIN';
export const MEAL_IDEA_SELECTED = 'MEAL_IDEA_SELECTED';
export const MEAL_IDEA_GROUP_REORDERED = 'MEAL_IDEA_GROUP_REORDERED';
export const MEAL_IDEA_GROUP_MEMBERS_CHANGED = 'MEAL_IDEA_GROUP_MEMBERS_CHANGED';
export const MEAL_IDEA_INGREDIENTS_REORDERED = 'MEAL_IDEA_INGREDIENTS_REORDERED';
/** A change shown ahead of its save is no longer: the save has answered, either way. */
export const MEAL_IDEA_SHOWN_AHEAD_SETTLED = 'MEAL_IDEA_SHOWN_AHEAD_SETTLED';

const IDEA_FIELDS = ['name', 'recipe_url', 'notes'];
const INGREDIENT_EXTRAS = ['quantity', 'notes'];

function errorMessage(error) {
	if(error && error.responseJSON && error.responseJSON.error) {
		return error.responseJSON.error;
	}
	if(error && error.error) {
		return error.error;
	}
	if(error && error.message) {
		return error.message;
	}
	return 'Something went wrong saving meal ideas.';
}

function loaded(response) {
	return { type: MEAL_IDEAS_LOADED, payload: response };
}

function failed(error) {
	console.error(error);
	return { type: MEAL_IDEAS_FAIL, payload: errorMessage(error) };
}

function post(dispatch, action, data) {
	dispatch({ type: MEAL_IDEAS_SAVE_BEGIN });
	return Request.apiv1Post(`meal_idea/${action}`, data)
		.then((response) => dispatch(loaded(response)))
		.catch((error) => dispatch(failed(error)));
}

/** The meal ideas as the screens show them: the food store's, with what is shown ahead of its save. */
const indexOf = (getState) => selectMealIdeasIndex(getState());

/** Stops showing `shown` for the group or idea `id` ahead of its save, unless something newer is shown since. */
const shownAheadSettled = (kind, id, shown) => ({
	type: MEAL_IDEA_SHOWN_AHEAD_SETTLED,
	payload: { kind, id: Number(id), shown },
});

function moveWithin(list, startIndex, endIndex) {
	const moved = [...list];
	const [item] = moved.splice(startIndex, 1);
	moved.splice(endIndex, 0, item);
	return moved;
}

/**
 * Every idea and group. They come with the food store's snapshot, through the food bridge, so this
 * waits for the refresh in flight or starts one. With no copy of the budget and a failed snapshot,
 * that is the snapshot asked for again: the legacy meal_idea/index is no longer called
 * (docs/local-first-food-data.md, phase 5).
 */
export const loadMealIdeas = () => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	return dispatch(whenFoodRefreshed());
};

/**
 * What the budget most often put in slot `type` in the three months before `date`, for the day
 * popup to suggest before anything is typed. Not kept in the store: it is only for that popup.
 *
 * @returns {Promise<{meal_idea_ids: number[], names: string[]}>}
 */
export const fetchCommonMeals = (date, type) => Request.apiv1Get('meal_idea/common', {
	type,
	date: date.format('YYYY-MM-DD'),
}).then((response) => ({
	meal_idea_ids: (response.meal_idea_ids || []).map(Number),
	names: response.names || [],
}));

/**
 * For the meal cleanup page: the names calendar meals carry with no meal idea, or with `skipped`
 * the names skipped there. Not kept in the store: only that page shows them.
 *
 * @returns {Promise<Array<{name: string, count: number, first: string, last: string,
 *          meal_idea_id: number|null, grocery_item_id: number|null}>>}
 */
export const fetchUnlinkedMealNames = (skipped = false) => Request.apiv1Get('meal_idea/unlinked_meal_names', skipped ? { skipped: 1 } : {})
	.then((response) => response.names || []);

/** Skips the name on the cleanup page, or with `skip` false brings it back. Resolves with the list it left. */
export const skipMealName = (name, skip = true) => Request.apiv1Post('meal_idea/skip_meal_name', { name, skip: skip ? 1 : 0 })
	.then((response) => response.names || []);

/** Opens the idea in the Meal Ideas tab's popup. */
export const selectMealIdea = (mealIdeaId) => ({
	type: MEAL_IDEA_SELECTED,
	payload: Number(mealIdeaId),
});

export const clearSelectedMealIdea = () => ({ type: MEAL_IDEA_SELECTED, payload: null });

/** Not selected once it exists: that would open its popup after every idea typed in. */
export const createMealIdea = (name, groupId = null) => (dispatch) => {
	const data = { name };
	if(groupId !== null && groupId !== undefined) {
		data.meal_idea_group_id = groupId;
	}
	return post(dispatch, 'save_idea', data);
};

/**
 * Put an existing idea on the calendar: a meal named after it, last in slot `type` on `date`, linked
 * back to it (createMeal). Resolves true once it is kept.
 */
export const addMealIdeaToDate = (date, type, idea) => createMeal(date, type, { name: idea.name, meal_idea_id: idea.id });

/**
 * Create an idea from the calendar's picker and put it on the day in one go. The idea is online-only,
 * as every meal_idea write is; the meal is an op once the idea exists, and takes the name the server
 * stored, which it has tidied.
 */
export const createMealIdeaOnDate = (date, type, name) => (dispatch, getState) => {
	return post(dispatch, 'save_idea', { name })
		.then((action) => {
			const idea = action && action.type === MEAL_IDEAS_LOADED ?
				indexOf(getState).ideasById[Number(action.payload.meal_idea_id)] : null;
			return idea ? dispatch(addMealIdeaToDate(date, type, idea)) : false;
		});
};

/**
 * Make `name`, which meals were put on the calendar with, a meal idea: `mealIdeaId`'s, or the one
 * already called that, or a new one. Every meal of the name with no idea links to it, on any day, and
 * so do the bought list rows of the grocery item of that name; `withIngredient` gives the idea that
 * item as an ingredient too. The food store is refreshed afterwards, the months on screen with it,
 * since meals and grocery lists both changed on the server. `date`, the day it was asked from, is
 * not needed for that.
 *
 * Resolves with `{ mealIdeaId, mealsLinked, rowsLinked }` once that reload is done, so a day showing
 * the meals shows them linked, or false when it was refused.
 */
export const convertMealName = (date, name, { mealIdeaId = null, withIngredient = false } = {}) => (dispatch, getState) => {
	const data = { name };
	if(mealIdeaId !== null && mealIdeaId !== undefined) {
		data.meal_idea_id = mealIdeaId;
	}
	if(withIngredient) {
		data.with_ingredient = 1;
	}
	return post(dispatch, 'convert_meal_name', data).then((action) => {
		if(!action || action.type !== MEAL_IDEAS_LOADED) {
			return false;
		}
		const result = {
			mealIdeaId: Number(action.payload.meal_idea_id),
			mealsLinked: Number(action.payload.meals_linked) || 0,
			rowsLinked: Number(action.payload.rows_linked) || 0,
		};
		return Promise.resolve(dispatch(refreshFood())).then(() => result, () => result);
	});
};

/** Only the fields present in `fields` are sent; the endpoint leaves the others alone. */
export const updateMealIdea = (mealIdeaId, fields) => (dispatch) => {
	const data = { meal_idea_id: mealIdeaId };
	IDEA_FIELDS.forEach((field) => {
		if(fields[field] !== undefined) {
			data[field] = fields[field];
		}
	});
	return post(dispatch, 'save_idea', data);
};

export const deleteMealIdea = (mealIdeaId) => (dispatch) => {
	return post(dispatch, 'delete_idea', { meal_idea_id: mealIdeaId });
};

/**
 * Each idea's ingredient saves, one at a time, per store. Every save posts the whole list and the
 * server archives what it leaves out, so two in flight at once would each drop what the other added.
 */
const ingredientSaves = new WeakMap();

/**
 * The whole ordered list, every time. Rows with an `id` are renamed or reordered, rows without one
 * are created (they get a `temporary_id` so the response can be matched up), and rows missing from
 * the list are archived on the server. `quantity` and `notes` ride along when a row has them, so
 * renaming or reordering here never drops what was entered elsewhere.
 *
 * `ingredients` may instead be a function of the idea's current list. It is called once the saves
 * before it have answered, so an edit made while another is saving is made to what that one saved.
 * Resolves with MEAL_IDEAS_LOADED, or with MEAL_IDEAS_FAIL when the save is refused.
 */
export const setMealIdeaIngredients = (mealIdeaId, ingredients) => (dispatch, getState) => {
	const send = () => {
		const idea = indexOf(getState).ideasById[mealIdeaId];
		const list = typeof ingredients === 'function' ? ingredients(idea ? idea.ingredients || [] : []) : ingredients;
		const rows = list.map((ingredient) => {
			const row = ingredient.id ?
				{ id: ingredient.id, name: ingredient.name } :
				{ temporary_id: ingredient.temporary_id || generateUuid(), name: ingredient.name };
			INGREDIENT_EXTRAS.forEach((field) => {
				if(ingredient[field] !== undefined && ingredient[field] !== null) {
					row[field] = ingredient[field];
				}
			});
			return row;
		});
		// The server has already rewritten this idea's items on the active grocery list, so the
		// food store is refreshed rather than left showing the old names.
		return post(dispatch, 'set_ingredients', { meal_idea_id: mealIdeaId, ingredients: rows })
			.then((action) => {
				if(action && action.type === MEAL_IDEAS_LOADED) {
					return dispatch(fetchGroceryLists()).then(() => action, () => action);
				}
				return action;
			});
	};
	const queues = ingredientSaves.get(getState) || {};
	ingredientSaves.set(getState, queues);
	// One that threw must not stop the ones after it, or leave the idea's queue behind for good.
	const saving = queues[mealIdeaId] ? queues[mealIdeaId].then(send, send) : send();
	queues[mealIdeaId] = saving;
	const done = () => {
		if(queues[mealIdeaId] === saving) {
			delete queues[mealIdeaId];
		}
	};
	saving.then(done, done);
	return saving;
};

export const saveMealIdeaGroup = (name, groupId = null) => (dispatch) => {
	const data = { name };
	if(groupId !== null && groupId !== undefined) {
		data.meal_idea_group_id = groupId;
	}
	return post(dispatch, 'save_group', data);
};

export const deleteMealIdeaGroup = (groupId) => (dispatch) => {
	return post(dispatch, 'delete_group', { meal_idea_group_id: groupId });
};

/** The whole ordered membership of a group: one call covers add, remove and reorder. */
export const setMealIdeaGroupMembers = (groupId, mealIdeaIds) => (dispatch) => {
	return post(dispatch, 'set_group_members', { meal_idea_group_id: groupId, meal_idea_ids: mealIdeaIds });
};

/** A group's members shown before they are saved, so a drop lands at once. */
const groupMembersChanged = (groupId, mealIdeaIds) => ({
	type: MEAL_IDEA_GROUP_MEMBERS_CHANGED,
	payload: { groupId, mealIdeaIds },
});

function membersOf(getState, groupId) {
	const group = indexOf(getState).groupsById[groupId];
	return group ? group.meal_idea_ids : [];
}

const postGroupMembers = (groupId, mealIdeaIds) =>
	Request.apiv1Post('meal_idea/set_group_members', { meal_idea_group_id: groupId, meal_idea_ids: mealIdeaIds });

/**
 * Saves a group's members after they are already on screen. However the save ends they stop being
 * shown ahead of it: the food store then has the answer, or, after a refusal, what it had before, so
 * a drop that did not take snaps back instead of looking saved. One shown since stays.
 */
const saveShownGroupMembers = (dispatch, groupId, mealIdeaIds) => {
	dispatch({ type: MEAL_IDEAS_SAVE_BEGIN });
	return postGroupMembers(groupId, mealIdeaIds)
		.then((response) => dispatch(loaded(response)))
		.catch((error) => dispatch(failed(error)))
		.then((action) => {
			dispatch(shownAheadSettled('groups', groupId, mealIdeaIds));
			return action;
		});
};

/** At `index` among the group's members, a drop's; at the end without one. */
export const addMealIdeaToGroup = (mealIdeaId, groupId, index = null) => (dispatch, getState) => {
	const current = membersOf(getState, groupId);
	if(current.includes(mealIdeaId)) {
		return Promise.resolve();
	}
	const members = [...current];
	members.splice(index === null || index === undefined ? members.length : index, 0, mealIdeaId);
	dispatch(groupMembersChanged(groupId, members));
	return saveShownGroupMembers(dispatch, groupId, members);
};

export const removeMealIdeaFromGroup = (mealIdeaId, groupId) => (dispatch, getState) => {
	const remaining = membersOf(getState, groupId).filter((id) => id !== mealIdeaId);
	return dispatch(setMealIdeaGroupMembers(groupId, remaining));
};

/**
 * A drop into another group: land at `index` in the destination, then leave the source. Both are
 * shown at once, and only the second save's response is loaded, since the first one's is the
 * moment the idea is in both groups.
 *
 * The destination goes first so that a failure part way never loses the idea. If its save is
 * refused, both groups snap back. If the source's is, the idea is in both groups on the server:
 * the destination as it was saved goes into the food store, and the source snaps back to show that.
 */
export const moveMealIdeaToGroup = (mealIdeaId, fromGroupId, toGroupId, index) => (dispatch, getState) => {
	const source = membersOf(getState, fromGroupId).filter((id) => id !== mealIdeaId);
	const destination = membersOf(getState, toGroupId).filter((id) => id !== mealIdeaId);
	destination.splice(index, 0, mealIdeaId);
	dispatch(groupMembersChanged(fromGroupId, source));
	dispatch(groupMembersChanged(toGroupId, destination));
	dispatch({ type: MEAL_IDEAS_SAVE_BEGIN });
	const savedDestination = () => {
		const food = getState().food;
		const group = food.entities.mealIdeaGroups[toGroupId];
		if(group) {
			dispatch(foodChangesReceived(food.budgetId, { upsert: { meal_idea_groups: [{ ...group, meal_idea_ids: destination }] } }));
		}
	};
	return postGroupMembers(toGroupId, destination)
		.then(
			() => postGroupMembers(fromGroupId, source)
				.then((response) => dispatch(loaded(response)))
				.catch((error) => {
					savedDestination();
					dispatch(failed(error));
				}),
			(error) => dispatch(failed(error))
		)
		.then(() => {
			dispatch(shownAheadSettled('groups', fromGroupId, source));
			dispatch(shownAheadSettled('groups', toGroupId, destination));
		});
};

/** Reorders are shown at once and saved after; a drop that snaps back and then jumps reads as a bug. */
export const reorderMealIdeaInGroup = (groupId, startIndex, endIndex) => (dispatch, getState) => {
	if(!indexOf(getState).groupsById[groupId]) {
		return Promise.resolve();
	}
	const members = moveWithin(membersOf(getState, groupId), startIndex, endIndex);
	dispatch({ type: MEAL_IDEA_GROUP_REORDERED, payload: { groupId, startIndex, endIndex, mealIdeaIds: members } });
	return saveShownGroupMembers(dispatch, groupId, members);
};

/**
 * Shown at once, then saved; a refused save moves it back, unless something since has moved it again.
 * The save finds the ingredient by id, since a save answering before it may have changed the list.
 */
export const reorderMealIdeaIngredient = (mealIdeaId, startIndex, endIndex) => (dispatch, getState) => {
	const idea = indexOf(getState).ideasById[mealIdeaId];
	const moved = idea && idea.ingredients[startIndex];
	if(!moved) {
		return Promise.resolve();
	}
	const positionOf = (list) => list.findIndex((row) => row.id === moved.id);
	const shown = moveWithin(idea.ingredients, startIndex, endIndex).map((ingredient) => ingredient.id);
	dispatch({ type: MEAL_IDEA_INGREDIENTS_REORDERED, payload: { mealIdeaId, startIndex, endIndex, ingredientIds: shown } });
	return dispatch(setMealIdeaIngredients(mealIdeaId, (list) => {
		const from = positionOf(list);
		return from < 0 ? list : moveWithin(list, from, Math.min(endIndex, list.length - 1));
	})).then((action) => {
		dispatch(shownAheadSettled('ingredients', mealIdeaId, shown));
		return action;
	});
};

/**
 * Asks the server to read the recipe page's title and image (task 5 owns the endpoint). The id is
 * positional in the url, which is how that endpoint is specified; a slow site can take ~15s.
 */
export const fetchMealIdeaRecipe = (mealIdeaId) => (dispatch) => {
	return post(dispatch, `fetch_recipe/${mealIdeaId}`, {});
};

/**
 * Runs one of the calendar module's grocery list thunks and resolves true when it dispatched
 * `successType`. Those thunks resolve the same way on success and failure, and the button needs to
 * say which.
 */
function reportingOutcome(thunk, successType = ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS) {
	return (dispatch, getState) => {
		let succeeded = false;
		const watching = (action) => {
			if(action && action.type === successType) {
				succeeded = true;
			}
			return dispatch(action);
		};
		return Promise.resolve(thunk(watching, getState)).then(() => succeeded);
	};
}

/**
 * Puts the idea's ingredients, or only `ingredientIds`, on the active grocery list; ones already there
 * from it are skipped.
 */
export const addMealIdeaToActiveList = (mealIdeaId, ingredientIds = null) =>
	reportingOutcome(addMealIdeaToGroceryList(mealIdeaId, ingredientIds));

export const addMealIdeaGroupToActiveList = (groupId) => reportingOutcome(addMealIdeaGroupToGroceryList(groupId));

/** Takes one of the idea's ingredients off the active grocery list, without checking it off. */
export const removeIngredientFromActiveList = (mealIdeaId, ingredientId) => reportingOutcome(
	removeMealIdeaIngredientFromGroceryList(mealIdeaId, ingredientId),
	REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS);

export { moveWithin };
